import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Badge, Button, Form, Spinner, Modal } from 'react-bootstrap';
import {
  GET_TASKS,
  GET_ADMIN_USERS,
  CREATE_TASK,
  UPDATE_TASK,
  DELETE_TASK,
} from '../../graphql/operations';
import TaskManagerModal from './TaskManagerModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const COLUMNS = [
  {
    key: 'todo',
    label: 'To Do',
    icon: 'fas fa-circle',
    color: '#64748b',
    bg: '#f1f5f9',
    border: '#cbd5e1',
  },
  {
    key: 'in_progress',
    label: 'In Progress',
    icon: 'fas fa-spinner',
    color: '#6366f1',
    bg: '#eef2ff',
    border: '#c7d2fe',
  },
  {
    key: 'done',
    label: 'Done',
    icon: 'fas fa-check-circle',
    color: '#10b981',
    bg: '#ecfdf5',
    border: '#a7f3d0',
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    icon: 'fas fa-times-circle',
    color: '#ef4444',
    bg: '#fef2f2',
    border: '#fecaca',
  },
];

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  low: { label: 'Low', color: '#10b981', bg: '#ecfdf5' },
  medium: { label: 'Medium', color: '#f59e0b', bg: '#fffbeb' },
  high: { label: 'High', color: '#f97316', bg: '#fff7ed' },
  urgent: { label: 'Urgent', color: '#ef4444', bg: '#fef2f2' },
};

const STAFF_ROLES = ['manager', 'admin', 'admin-manager', 'operator', 'designer', 'employee', 'staff'];

// ─── Helper Functions ─────────────────────────────────────────────────────────

const formatDate = (dateStr: string) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isDueSoon = (dateStr: string) => {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays >= 0 && diffDays <= 3;
};

const isOverdue = (dateStr: string, status: string) => {
  if (!dateStr || status === 'done' || status === 'cancelled') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dateStr) < today;
};

const getInitials = (username: string) => {
  return (username || '?')
    .split(/[\s_.-]+/)
    .map(w => w[0]?.toUpperCase() || '')
    .slice(0, 2)
    .join('');
};

const AVATAR_PALETTE = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f97316',
  '#10b981', '#0ea5e9', '#f43f5e', '#84cc16',
];
const getAvatarColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

// ─── TaskCard Component ───────────────────────────────────────────────────────

interface TaskCardProps {
  task: any;
  onEdit: (task: any) => void;
  onDelete: (task: any) => void;
  onStatusChange: (task: any, status: string) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onEdit, onDelete, onStatusChange }) => {
  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const overdue = isOverdue(task.dueDate, task.status);
  const dueSoon = isDueSoon(task.dueDate);
  const assignee = task.assignedToUser;
  const initials = getInitials(assignee?.username || '?');
  const avatarColor = getAvatarColor(assignee?.username || 'x');

  const nextStatus: Record<string, string> = {
    todo: 'in_progress',
    in_progress: 'done',
    done: 'todo',
    cancelled: 'todo',
  };

  const prevStatus: Record<string, string> = {
    in_progress: 'todo',
    done: 'in_progress',
    cancelled: 'in_progress',
  };

  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        padding: '1rem',
        marginBottom: '0.75rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.2s, transform 0.2s',
        cursor: 'default',
        position: 'relative',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
      }}
    >
      {/* Priority accent bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          background: priority.color,
          borderRadius: '12px 0 0 12px',
        }}
      />

      <div style={{ paddingLeft: '4px' }}>
        {/* Header row */}
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: '#94a3b8',
                fontFamily: 'monospace',
                letterSpacing: '0.03em',
              }}
            >
              {task.taskId}
            </span>
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: priority.color,
                background: priority.bg,
                borderRadius: '20px',
                padding: '1px 8px',
                border: `1px solid ${priority.color}30`,
              }}
            >
              {priority.label}
            </span>
            {task.category && (
              <span
                style={{
                  fontSize: '0.7rem',
                  color: '#6366f1',
                  background: '#eef2ff',
                  borderRadius: '20px',
                  padding: '1px 8px',
                  fontWeight: 600,
                }}
              >
                {task.category}
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div className="d-flex gap-1">
            <button
              onClick={() => onEdit(task)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                padding: '2px 5px',
                cursor: 'pointer',
                borderRadius: '6px',
                transition: 'color 0.15s, background 0.15s',
              }}
              title="Edit task"
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#6366f1'; (e.currentTarget as HTMLElement).style.background = '#eef2ff'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#94a3b8'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <i className="fas fa-pen" style={{ fontSize: '0.75rem' }} />
            </button>
            <button
              onClick={() => onDelete(task)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                padding: '2px 5px',
                cursor: 'pointer',
                borderRadius: '6px',
                transition: 'color 0.15s, background 0.15s',
              }}
              title="Delete task"
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#ef4444'; (e.currentTarget as HTMLElement).style.background = '#fef2f2'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#94a3b8'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <i className="fas fa-trash" style={{ fontSize: '0.75rem' }} />
            </button>
          </div>
        </div>

        {/* Title */}
        <div className="fw-semibold mb-1" style={{ fontSize: '0.9rem', color: '#1e293b', lineHeight: 1.35 }}>
          {task.title}
        </div>

        {/* Description */}
        {task.description && (
          <div
            style={{
              fontSize: '0.8rem',
              color: '#64748b',
              lineHeight: 1.4,
              marginBottom: '0.6rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {task.description}
          </div>
        )}

        {/* Footer row */}
        <div className="d-flex justify-content-between align-items-center mt-2">
          {/* Assignee */}
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: avatarColor,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.65rem',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {initials}
            </div>
            <span style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 500 }}>
              {assignee?.username || 'Unassigned'}
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            {/* Due date */}
            {task.dueDate && (
              <span
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: overdue ? '#ef4444' : dueSoon ? '#f97316' : '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <i className={`fas fa-${overdue ? 'exclamation-triangle' : 'calendar-alt'}`} />
                {formatDate(task.dueDate)}
              </span>
            )}

            {/* Back button — move to previous status */}
            {prevStatus[task.status] && (
              <button
                onClick={() => onStatusChange(task, prevStatus[task.status])}
                style={{
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '20px',
                  border: '1.5px solid #94a3b8',
                  background: 'transparent',
                  color: '#64748b',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#64748b'; (e.currentTarget as HTMLElement).style.color = '#fff'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#64748b'; }}
                title={`Move back to ${prevStatus[task.status].replace('_', ' ')}`}
              >
                <i className="fas fa-arrow-left me-1" />
                {/* Back */}
              </button>
            )}

            {/* Quick status advance button */}
            {task.status !== 'done' && task.status !== 'cancelled' && (
              <button
                onClick={() => onStatusChange(task, nextStatus[task.status])}
                style={{
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '20px',
                  border: '1.5px solid #10b981',
                  background: 'transparent',
                  color: '#10b981',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#10b981'; (e.currentTarget as HTMLElement).style.color = '#fff'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#10b981'; }}
                title={`Mark as ${nextStatus[task.status].replace('_', ' ')}`}
              >
                <i className="fas fa-arrow-right me-1" />
                {/* {nextStatus[task.status] === 'in_progress' ? 'Start' : 'Done'} */}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main TaskManager Component ───────────────────────────────────────────────

interface TaskManagerProps {
  triggerToast: (message: string, variant?: 'success' | 'danger' | 'warning' | 'info') => void;
}

const TaskManager: React.FC<TaskManagerProps> = ({ triggerToast }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [taskToDelete, setTaskToDelete] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'kanban' | 'table'>('kanban');
  const [searchText, setSearchText] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Queries
  const { data: tasksData, loading: tasksLoading, refetch: refetchTasks } = useQuery(GET_TASKS, {
    fetchPolicy: 'network-only',
  });

  const { data: usersData } = useQuery(GET_ADMIN_USERS, {
    fetchPolicy: 'network-only',
  });

  // Mutations
  const [createTaskMutation] = useMutation(CREATE_TASK, {
    onCompleted: () => refetchTasks(),
  });
  const [updateTaskMutation] = useMutation(UPDATE_TASK, {
    onCompleted: () => refetchTasks(),
  });
  const [deleteTaskMutation] = useMutation(DELETE_TASK, {
    onCompleted: () => refetchTasks(),
  });

  const allTasks: any[] = tasksData?.tasks || [];
  const allUsers: any[] = usersData?.users || [];
  const employees = allUsers.filter(u => STAFF_ROLES.includes(u.role));

  // Filtering
  const filteredTasks = useMemo(() => {
    return allTasks.filter(t => {
      if (searchText) {
        const q = searchText.toLowerCase();
        if (
          !t.title.toLowerCase().includes(q) &&
          !(t.description || '').toLowerCase().includes(q) &&
          !(t.assignedToUser?.username || '').toLowerCase().includes(q)
        ) return false;
      }
      if (filterEmployee !== 'all' && t.assignedTo !== filterEmployee) return false;
      if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
      if (filterStatus !== 'all' && t.status !== filterStatus) return false;
      return true;
    });
  }, [allTasks, searchText, filterEmployee, filterPriority, filterStatus]);

  // Stats
  const stats = useMemo(() => {
    const total = allTasks.length;
    const todo = allTasks.filter(t => t.status === 'todo').length;
    const inProgress = allTasks.filter(t => t.status === 'in_progress').length;
    const done = allTasks.filter(t => t.status === 'done').length;
    const overdue = allTasks.filter(t => isOverdue(t.dueDate, t.status)).length;
    return { total, todo, inProgress, done, overdue };
  }, [allTasks]);

  // Handlers
  const handleCreateTask = async (formData: any) => {
    const result = await createTaskMutation({
      variables: {
        title: formData.title,
        description: formData.description,
        assignedTo: formData.assignedTo,
        priority: formData.priority,
        dueDate: formData.dueDate || null,
        category: formData.category || null,
        notes: formData.notes || null,
      },
    });
    const res = result.data?.createTask;
    if (res?.success) {
      triggerToast('Task created successfully!', 'success');
    } else {
      throw new Error(res?.message || 'Failed to create task.');
    }
  };

  const handleUpdateTask = async (formData: any) => {
    const result = await updateTaskMutation({
      variables: {
        id: editingTask.id,
        title: formData.title,
        description: formData.description,
        assignedTo: formData.assignedTo,
        priority: formData.priority,
        status: formData.status,
        dueDate: formData.dueDate || null,
        category: formData.category || null,
        notes: formData.notes || null,
      },
    });
    const res = result.data?.updateTask;
    if (res?.success) {
      triggerToast('Task updated successfully!', 'success');
    } else {
      throw new Error(res?.message || 'Failed to update task.');
    }
  };

  const handleQuickStatusChange = async (task: any, newStatus: string) => {
    try {
      const result = await updateTaskMutation({
        variables: { id: task.id, status: newStatus },
      });
      const res = result.data?.updateTask;
      if (res?.success) {
        triggerToast(`Task marked as ${newStatus.replace('_', ' ')}!`, 'success');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to update task.', 'danger');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;
    try {
      const result = await deleteTaskMutation({ variables: { id: taskToDelete.id } });
      const res = result.data?.deleteTask;
      if (res?.success) {
        triggerToast('Task deleted.', 'info');
      }
    } catch (err: any) {
      triggerToast(err.message || 'Failed to delete task.', 'danger');
    } finally {
      setTaskToDelete(null);
    }
  };

  const openEdit = (task: any) => {
    setEditingTask(task);
    setShowModal(true);
  };

  const openCreate = () => {
    setEditingTask(null);
    setShowModal(true);
  };

  const tasksByColumn = (colKey: string) =>
    filteredTasks.filter(t => t.status === colKey);

  // ── Stat card ──
  const StatCard = ({ icon, label, value, color }: { icon: string; label: string; value: number; color: string }) => (
    <div
      style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '1rem 1.25rem',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        flex: 1,
        minWidth: 110,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '10px',
          background: `${color}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <i className={icon} style={{ color, fontSize: '1rem' }} />
      </div>
      <div>
        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '0.25rem 0' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h4
            className="mb-0 fw-bold"
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '1.5rem',
            }}
          >
            <i className="fas fa-tasks me-2" style={{ WebkitTextFillColor: '#6366f1' }} />
            Task Manager
          </h4>
          <p className="mb-0 mt-1" style={{ fontSize: '0.83rem', color: '#64748b' }}>
            Assign and track tasks across your team
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={() => setViewMode(v => v === 'kanban' ? 'table' : 'kanban')}
            style={{ borderRadius: '10px', fontSize: '0.83rem', padding: '0.45rem 0.9rem' }}
          >
            <i className={`fas ${viewMode === 'kanban' ? 'fa-table' : 'fa-columns'} me-2`} />
            {viewMode === 'kanban' ? 'Table View' : 'Kanban View'}
          </Button>
          <Button
            onClick={openCreate}
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              border: 'none',
              borderRadius: '10px',
              fontWeight: 600,
              fontSize: '0.83rem',
              padding: '0.45rem 1rem',
            }}
          >
            <i className="fas fa-plus me-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="d-flex gap-3 mb-4 flex-wrap">
        <StatCard icon="fas fa-tasks" label="Total Tasks" value={stats.total} color="#6366f1" />
        <StatCard icon="fas fa-circle" label="To Do" value={stats.todo} color="#64748b" />
        <StatCard icon="fas fa-spinner" label="In Progress" value={stats.inProgress} color="#6366f1" />
        <StatCard icon="fas fa-check-circle" label="Completed" value={stats.done} color="#10b981" />
        <StatCard icon="fas fa-exclamation-triangle" label="Overdue" value={stats.overdue} color="#ef4444" />
      </div>

      {/* Filters */}
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          padding: '0.875rem 1rem',
          marginBottom: '1.25rem',
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <div style={{ position: 'relative', flex: '1', minWidth: 180 }}>
          <i
            className="fas fa-search"
            style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.8rem' }}
          />
          <Form.Control
            type="text"
            placeholder="Search tasks or employees..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{
              paddingLeft: '2.1rem',
              borderRadius: '8px',
              border: '1.5px solid #e2e8f0',
              fontSize: '0.83rem',
              height: '36px',
            }}
          />
        </div>

        <Form.Select
          value={filterEmployee}
          onChange={e => setFilterEmployee(e.target.value)}
          style={{ maxWidth: 160, borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '0.83rem', height: '36px' }}
        >
          <option value="all">All Employees</option>
          {employees.map(emp => (
            <option key={emp.id} value={emp.id}>{emp.username}</option>
          ))}
        </Form.Select>

        <Form.Select
          value={filterPriority}
          onChange={e => setFilterPriority(e.target.value)}
          style={{ maxWidth: 130, borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '0.83rem', height: '36px' }}
        >
          <option value="all">All Priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </Form.Select>

        <Form.Select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{ maxWidth: 130, borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '0.83rem', height: '36px' }}
        >
          <option value="all">All Statuses</option>
          <option value="todo">To Do</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
          <option value="cancelled">Cancelled</option>
        </Form.Select>

        {(searchText || filterEmployee !== 'all' || filterPriority !== 'all' || filterStatus !== 'all') && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => { setSearchText(''); setFilterEmployee('all'); setFilterPriority('all'); setFilterStatus('all'); }}
            style={{ borderRadius: '8px', fontSize: '0.78rem', height: '36px' }}
          >
            <i className="fas fa-times me-1" />Clear
          </Button>
        )}
      </div>

      {/* Loading */}
      {tasksLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: '#6366f1' }} />
          <div className="mt-2" style={{ color: '#64748b', fontSize: '0.875rem' }}>Loading tasks...</div>
        </div>
      )}

      {/* ── Kanban Board ── */}
      {!tasksLoading && viewMode === 'kanban' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1rem',
            alignItems: 'start',
          }}
        >
          {COLUMNS.map(col => {
            const cards = tasksByColumn(col.key);
            return (
              <div
                key={col.key}
                style={{
                  background: col.bg,
                  borderRadius: '14px',
                  border: `2px solid ${col.border}`,
                  padding: '1rem',
                  minHeight: 200,
                }}
              >
                {/* Column header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <i className={col.icon} style={{ color: col.color, fontSize: '0.9rem' }} />
                    <span style={{ fontWeight: 700, fontSize: '0.88rem', color: col.color }}>
                      {col.label}
                    </span>
                  </div>
                  <span
                    style={{
                      background: col.color,
                      color: '#fff',
                      borderRadius: '20px',
                      padding: '1px 10px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                    }}
                  >
                    {cards.length}
                  </span>
                </div>

                {/* Cards */}
                {cards.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      color: '#94a3b8',
                      fontSize: '0.8rem',
                      padding: '1.5rem 0',
                      border: `2px dashed ${col.border}`,
                      borderRadius: '8px',
                    }}
                  >
                    <i className="fas fa-inbox mb-2" style={{ fontSize: '1.2rem', display: 'block' }} />
                    No tasks
                  </div>
                ) : (
                  cards.map(task => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onEdit={openEdit}
                      onDelete={setTaskToDelete}
                      onStatusChange={handleQuickStatusChange}
                    />
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Table View ── */}
      {!tasksLoading && viewMode === 'table' && (
        <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {filteredTasks.length === 0 ? (
            <div className="text-center py-5" style={{ color: '#94a3b8' }}>
              <i className="fas fa-tasks" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }} />
              No tasks found
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
                    {['Task ID', 'Title', 'Assigned To', 'Category', 'Priority', 'Status', 'Due Date', 'Actions'].map(h => (
                      <th
                        key={h}
                        style={{
                          padding: '0.75rem 1rem',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map((task, idx) => {
                    const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                    const col = COLUMNS.find(c => c.key === task.status);
                    const overdue = isOverdue(task.dueDate, task.status);
                    const assignee = task.assignedToUser;
                    const initials = getInitials(assignee?.username || '?');
                    const avatarColor = getAvatarColor(assignee?.username || 'x');

                    return (
                      <tr
                        key={task.id}
                        style={{
                          background: idx % 2 === 0 ? '#fff' : '#f8fafc',
                          borderBottom: '1px solid #f1f5f9',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = '#eef2ff'}
                        onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = idx % 2 === 0 ? '#fff' : '#f8fafc'}
                      >
                        <td style={{ padding: '0.7rem 1rem', fontSize: '0.72rem', fontFamily: 'monospace', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                          {task.taskId}
                        </td>
                        <td style={{ padding: '0.7rem 1rem', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', maxWidth: 220 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</div>
                          {task.description && (
                            <div style={{ fontSize: '0.75rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {task.description}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '0.7rem 1rem' }}>
                          <div className="d-flex align-items-center gap-2">
                            <div style={{ width: 24, height: 24, borderRadius: '50%', background: avatarColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 }}>
                              {initials}
                            </div>
                            <span style={{ fontSize: '0.82rem', fontWeight: 500, color: '#475569', whiteSpace: 'nowrap' }}>
                              {assignee?.username || '—'}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '0.7rem 1rem' }}>
                          {task.category ? (
                            <span style={{ fontSize: '0.73rem', color: '#6366f1', background: '#eef2ff', borderRadius: '20px', padding: '2px 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {task.category}
                            </span>
                          ) : <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.7rem 1rem' }}>
                          <span style={{ fontSize: '0.73rem', color: priority.color, background: priority.bg, borderRadius: '20px', padding: '2px 10px', fontWeight: 700, border: `1px solid ${priority.color}30`, whiteSpace: 'nowrap' }}>
                            {priority.label}
                          </span>
                        </td>
                        <td style={{ padding: '0.7rem 1rem' }}>
                          {col && (
                            <span style={{ fontSize: '0.73rem', color: col.color, background: col.bg, borderRadius: '20px', padding: '2px 10px', fontWeight: 600, border: `1px solid ${col.border}`, whiteSpace: 'nowrap' }}>
                              <i className={`${col.icon} me-1`} />{col.label}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '0.7rem 1rem', fontSize: '0.8rem', fontWeight: overdue ? 700 : 400, color: overdue ? '#ef4444' : '#64748b', whiteSpace: 'nowrap' }}>
                          {task.dueDate ? (
                            <>
                              {overdue && <i className="fas fa-exclamation-triangle me-1" />}
                              {formatDate(task.dueDate)}
                            </>
                          ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.7rem 1rem' }}>
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => openEdit(task)}
                              style={{ borderRadius: '6px', padding: '2px 8px', fontSize: '0.75rem' }}
                            >
                              <i className="fas fa-edit" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => setTaskToDelete(task)}
                              style={{ borderRadius: '6px', padding: '2px 8px', fontSize: '0.75rem' }}
                            >
                              <i className="fas fa-trash" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Task Create/Edit Modal */}
      <TaskManagerModal
        show={showModal}
        onHide={() => { setShowModal(false); setEditingTask(null); }}
        onSubmit={editingTask ? handleUpdateTask : handleCreateTask}
        task={editingTask}
        employees={employees}
        isEditing={!!editingTask}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={!!taskToDelete} onHide={() => setTaskToDelete(null)} centered size="sm">
        <Modal.Body className="text-center p-4">
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
            }}
          >
            <i className="fas fa-trash" style={{ color: '#ef4444', fontSize: '1.4rem' }} />
          </div>
          <h6 className="fw-bold mb-1">Delete Task?</h6>
          <p style={{ fontSize: '0.83rem', color: '#64748b' }} className="mb-3">
            "<strong>{taskToDelete?.title}</strong>" will be permanently removed.
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <Button variant="light" size="sm" onClick={() => setTaskToDelete(null)} style={{ borderRadius: '8px', minWidth: 80 }}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleDeleteConfirm}
              style={{ borderRadius: '8px', minWidth: 80, background: '#ef4444', border: 'none', fontWeight: 600 }}
            >
              Delete
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TaskManager;
